import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Collapse, FormControlLabel, LinearProgress, MenuItem, Select, Stack, Switch, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { downloadDiagnosticWav } from './audioDiagnostics';
import { buildVoiceWebSocketUrl, parseVoiceEvent, type VoicePatientPatch, type VoiceStatus } from './voiceClient';

const CHUNK_MS = 160;
const PREROLL_MS = 640;
const workletSource = `
class AvatarPCMProcessor extends AudioWorkletProcessor {
  constructor(){super();this.parts=[];this.length=0;this.target=Math.max(128,Math.round(sampleRate*${CHUNK_MS}/1000))}
  process(inputs){const input=inputs[0]&&inputs[0][0];if(!input)return true;this.parts.push(new Float32Array(input));this.length+=input.length;if(this.length<this.target)return true;const pcm=new Int16Array(this.length);let offset=0,energy=0;for(const part of this.parts)for(let i=0;i<part.length;i++){const sample=Math.max(-1,Math.min(1,part[i]));energy+=sample*sample;pcm[offset++]=sample<0?sample*32768:sample*32767}this.port.postMessage({buffer:pcm.buffer,level:Math.min(1,Math.sqrt(energy/pcm.length)*5)},[pcm.buffer]);this.parts=[];this.length=0;return true}
}
registerProcessor('avatar-pcm',AvatarPCMProcessor)`;

interface AudioRecorderProps {
  onAudioReady: (blob: Blob, filename: string) => void;
  onFinalTranscript: (text: string) => void;
  onPartialTranscript?: (text: string) => void;
  onPatientPatch?: (patch: VoicePatientPatch) => void;
}

const labels: Record<VoiceStatus, string> = { idle: '', connecting: 'Подключение к распознаванию…', recording: 'Идёт запись', processing: 'Распознавание…' };

export default function AudioRecorder({ onAudioReady, onFinalTranscript, onPartialTranscript, onPatientPatch }: AudioRecorderProps) {
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [diagnostics, setDiagnostics] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [level, setLevel] = useState(0);
  const [diagnosticReady, setDiagnosticReady] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const nodesRef = useRef<AudioNode[]>([]);
  const enabledRef = useRef(false);
  const stoppingRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const preRollRef = useRef<Int16Array[]>([]);
  const diagnosticRef = useRef<Int16Array[]>([]);
  const diagnosticRateRef = useRef(16000);

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const inputs = (await navigator.mediaDevices.enumerateDevices()).filter((item) => item.kind === 'audioinput');
    setDevices(inputs); setDeviceId((current) => current || inputs[0]?.deviceId || '');
  }, []);
  useEffect(() => { void refreshDevices(); }, [refreshDevices]);

  const cleanup = useCallback((closeSocket = true) => {
    enabledRef.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    nodesRef.current.forEach((node) => node.disconnect());
    if (contextRef.current?.state !== 'closed') void contextRef.current?.close();
    if (closeSocket && socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) socketRef.current.close(1000);
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    socketRef.current = null; streamRef.current = null; contextRef.current = null; nodesRef.current = [];
    timerRef.current = null; preRollRef.current = []; setLevel(0); onPartialTranscript?.('');
  }, [onPartialTranscript]);
  useEffect(() => () => cleanup(), [cleanup]);

  const start = async () => {
    setError(null); setStatus('connecting'); setDiagnosticReady(false);
    diagnosticRef.current = []; preRollRef.current = []; stoppingRef.current = false;
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('Браузер не поддерживает запись с микрофона');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: deviceId ? { exact: deviceId } : undefined, channelCount: 1, echoCancellation: processing, noiseSuppression: processing, autoGainControl: processing } });
      await refreshDevices();
      // PCM16 at 24 kHz is split into short WAV chunks by the backend.
      const context = new AudioContext({ sampleRate: 24000 }); diagnosticRateRef.current = context.sampleRate;
      const url = URL.createObjectURL(new Blob([workletSource], { type: 'text/javascript' }));
      try { await context.audioWorklet.addModule(url); } finally { URL.revokeObjectURL(url); }
      const source = context.createMediaStreamSource(stream);
      const inputGain = context.createGain(); inputGain.gain.value = 1.35;
      const worklet = new AudioWorkletNode(context, 'avatar-pcm');
      const monitorGain = context.createGain(); monitorGain.gain.value = 0;
      source.connect(inputGain).connect(worklet).connect(monitorGain).connect(context.destination);
      streamRef.current = stream; contextRef.current = context; nodesRef.current = [source, inputGain, worklet, monitorGain];
      const socket = new WebSocket(buildVoiceWebSocketUrl()); socket.binaryType = 'arraybuffer'; socketRef.current = socket;
      const maxPreRoll = Math.ceil(PREROLL_MS / CHUNK_MS);
      worklet.port.onmessage = (event: MessageEvent<{ buffer: ArrayBuffer; level: number }>) => {
        const chunk = new Int16Array(event.data.buffer); setLevel(event.data.level);
        if (diagnostics) diagnosticRef.current.push(chunk.slice());
        if (enabledRef.current && socket.readyState === WebSocket.OPEN) socket.send(chunk.buffer);
        else if (!stoppingRef.current) { preRollRef.current.push(chunk.slice()); if (preRollRef.current.length > maxPreRoll) preRollRef.current.shift(); }
      };
      socket.onopen = () => socket.send(JSON.stringify({ type: 'voice.start', sample_rate: context.sampleRate }));
      socket.onmessage = (event: MessageEvent<string>) => {
        const message = parseVoiceEvent(event.data); if (!message) return;
        if (message.type === 'voice.status' && message.status) {
          setStatus(message.status);
          if (message.status === 'recording') { enabledRef.current = true; for (const chunk of preRollRef.current) socket.send(chunk.buffer); preRollRef.current = []; }
        } else if (message.type === 'transcript.partial') onPartialTranscript?.(message.text ?? '');
        else if (message.type === 'patient.patch' && message.patch) onPatientPatch?.(message.patch);
        else if (message.type === 'transcript.final') {
          onPartialTranscript?.(''); if (message.text) onFinalTranscript(message.text);
          if (stoppingRef.current) { if (diagnostics && diagnosticRef.current.length) setDiagnosticReady(true); cleanup(); setStatus('idle'); }
        } else if (message.type === 'voice.error') { setError(message.message ?? 'Не удалось распознать речь'); cleanup(); setStatus('idle'); }
      };
      socket.onerror = () => { setError('Не удалось подключиться к потоковому распознаванию'); cleanup(false); setStatus('idle'); };
      socket.onclose = () => { enabledRef.current = false; setStatus('idle'); };
    } catch (cause) { cleanup(); setStatus('idle'); setError(cause instanceof Error ? cause.message : 'Не удалось начать запись'); }
  };

  const stop = () => {
    const socket = socketRef.current; stoppingRef.current = true; enabledRef.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop()); setStatus('processing');
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'voice.stop' }));
      timerRef.current = window.setTimeout(() => { if (diagnostics && diagnosticRef.current.length) setDiagnosticReady(true); cleanup(); setStatus('idle'); }, 8000);
    } else { cleanup(); setStatus('idle'); }
  };

  const active = status !== 'idle';
  return <Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
      <Button variant={status === 'recording' ? 'contained' : 'outlined'} color={status === 'recording' ? 'error' : 'primary'} startIcon={status === 'recording' ? <StopIcon /> : <MicIcon />} onClick={active ? stop : start} disabled={status === 'processing'}>{status === 'recording' ? 'Остановить запись' : status === 'connecting' ? 'Отменить' : 'Распознавать с микрофона'}</Button>
      <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={active}>Загрузить аудио<input type="file" accept="audio/*" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) onAudioReady(file, file.name); event.target.value = ''; }} /></Button>
      <Button size="small" onClick={() => setSettingsOpen((value) => !value)} disabled={active}>Настройки микрофона</Button>
      {labels[status] && <Typography variant="body2" color="text.secondary">{labels[status]}</Typography>}
    </Stack>
    {active && <LinearProgress variant="determinate" value={Math.max(2, level * 100)} color={level > .02 ? 'success' : 'primary'} sx={{ mt: 1, maxWidth: 360, height: 5, borderRadius: 5 }} />}
    <Collapse in={settingsOpen}><Stack spacing={.5} sx={{ mt: 1, maxWidth: 520 }}>
      <Select size="small" value={deviceId} onChange={(event) => setDeviceId(event.target.value)} displayEmpty>{devices.map((item, index) => <MenuItem key={item.deviceId} value={item.deviceId}>{item.label || `Микрофон ${index + 1}`}</MenuItem>)}</Select>
      <FormControlLabel control={<Switch checked={processing} onChange={(_, value) => setProcessing(value)} />} label="Подавление шума и эха" />
      <FormControlLabel control={<Switch checked={diagnostics} onChange={(_, value) => setDiagnostics(value)} />} label="Подготовить тестовую WAV-запись" />
      <Typography variant="caption" color="text.secondary">Диагностическая запись остаётся в браузере.</Typography>
    </Stack></Collapse>
    {diagnosticReady && <Button size="small" onClick={() => downloadDiagnosticWav(diagnosticRef.current, diagnosticRateRef.current)}>Скачать тестовую запись WAV</Button>}
    {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
  </Box>;
}
